import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import StructureIcon from '~/assets/button/menutree.svg';
import { LEFT_MENU_WIDTH } from '~/editor/constants';

const StructureIconForBtn = styled(StructureIcon)`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: #333;
  display: block;
  overflow: visible;
`;

const ViewAllBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fafafa;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 15px;
  font-family: 'Inter', 'Helvetica Neue', sans-serif;
  cursor: pointer;
  font-weight: 500;
  color: #1a1a1a;
  border: 1px solid #d4d4d4;
  transition: all 0.15s ease;
  width: 100%;
  text-align: left;

  &:hover {
    background: #f0f0f0;
    border-color: #bbb;
  }

  &:focus {
    outline: none;
  }

  &.active {
    background: #f5f5f5;
    border-color: #bbb;
  }
`;
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';
import DotsIcon from '~/assets/button/dotsVertical.svg';
import { CreateNew } from './CreateNew';
import PopupMenuForChapter from '~/editor/popupMenu/PopupMenuForChapter';
import { useEditorStore, EditorChapter, EditorTraining } from '~/shell/Store';
import { useMoveStore } from '~/editor/store/moveElementStore';
import { SequenceItem } from './SequenceItem';

const DotsIconForBtn = styled(DotsIcon)`
  cursor: pointer;
  margin-top: 5px;

  position: absolute;
  right: 5px;
  width: 16px;
  padding-left: 4px;
`;

const DragAndDropIconForBtn = styled(DragAndDropIcon)`
  cursor: move;
`;

const LeftPanel = styled.div`
  z-index: 199;
  box-shadow: rgba(0, 0, 0, 0.31) 3px 3px 5px;
`;

const Wrapper = styled.div`
  background: #fff;
  width: ${LEFT_MENU_WIDTH}px;
  height: calc(100vh - 60px);
  overflow-y: auto;
  padding-top: 10px;

  .chapterItemElement {
    position: relative;
    border-bottom: 2px solid transparent;
    border-top: 2px solid transparent;

    &.drag-over-top {
      border-top: 2px solid #9845fb !important;
    }

    &.drag-over-bottom {
      border-top: 2px solid transparent !important;
      border-bottom: 2px solid #9845fb !important;
    }
  }

  .sequenceItem {
    position: relative;
    display: flex;
    min-height: 52px;
    padding: 10px;
    padding-right: 10px;
    padding-left: 20px;
    margin-left: 8px;
    margin-right: 5px;
    cursor: pointer;
    align-items: center;
    border-bottom: 2px solid transparent;
    border-top: 2px solid transparent;

    &.drag-over-top {
      border-top: 2px solid #9845fb !important;
      border-bottom: 2px solid transparent !important;
    }

    &.drag-over-bottom {
      border-top: 2px solid transparent !important;
      border-bottom: 2px solid #9845fb !important;
    }

    div {
      flex: 1;
      font-size: 12px;
      line-height: 28px;
      overflow: hidden;
      margin-left: 8px;
      padding-right: 5px;
    }

    svg {
      path {
        fill: #a9a9a9;
      }
    }
  }
  .sequenceItem.active {
    background-color: #f5f5f5;
    border-radius: 8px;
  }
`;

const ChapterTitle = styled.div`
  position: relative;
  margin-left: 10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;

  svg {
    margin-top: 0px;
    margin-right: 10px;
  }

  .chapter_title_wrapper {
    flex: 1;
  }
  button.chapter_title_wrapper {
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  button.chapter_title_wrapper:focus {
    outline: none;
  }
  .chapter_title_wrapper.active {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 4px 6px;
  }

  .chapter_number {
    font-family: Raleway !important;
    font-size: 10px !important;
    font-weight: 400 !important;
    line-height: 15px;
    letter-spacing: 0.05em;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: #2e2e2e;
    text-transform: uppercase;
  }
  .chapter_title {
    font-family: ralewayBold;
    font-size: 12px;
    font-weight: unset !important;
    line-height: 18px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: #2e2e2e;
    margin-right: 26px;

    input {
      width: 98%;
    }

    input:focus {
      outline: 2px solid #9845fb;
    }
  }

  .chapter_dots {
    width: 10px;

    path {
      fill: #a9a9a9 !important;
    }
  }
`;

const ChapterName = ({
  el,
  setDraggable
}: {
  el: EditorChapter;
  setDraggable: (draggable: boolean) => void;
}) => {
  const { training, setTraining, apiService, markContentEdited } =
    useEditorStore();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState(el.title as string);

  useEffect(() => {
    if (el._rename && inputRef.current) {
      inputRef.current.focus();
    }
  }, [el._rename]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  const handleBlur = () => {
    setDraggable(true);
    if (text.trim() === '') {
      setText(el.title as string);
      return;
    }

    if (apiService) {
      apiService.updateChapter(el.id as number, { title: text });
    }

    const titleChanged = text !== el.title;

    const updatedChapters = training!.chapters.map((chapter: EditorChapter) => {
      if (chapter.id == el.id) {
        if (chapter._rename) {
          delete chapter._rename;
        }
        return { ...chapter, title: text };
      }
      return chapter;
    });
    setTraining({ ...training!, chapters: updatedChapters });

    // Renaming a chapter is a content edit — enable the Publish button.
    if (titleChanged) {
      markContentEdited();
    }
  };

  return (
    <div className="chapter_title">
      {el._rename ? (
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => setDraggable(false)}
        />
      ) : (
        el.title
      )}
    </div>
  );
};

interface LeftMenuProps {
  mode?: 'editor' | 'preview' | 'translation';
}

export const LeftMenu: React.FC<LeftMenuProps> = ({ mode = 'editor' }) => {
  const {
    training,
    setSequence,
    setTraining,
    apiService,
    createNewChapter,
    lastChapterId,
    hasFeatureFlag,
    setUndoRedoState,
    translationScope,
    setTranslationScope,
    markContentEdited
  } = useEditorStore();

  const [chapterData, setChapterData] = useState<EditorChapter | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);
  const [isMove, setIsMove] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [draggable, setDraggable] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [classNameForDragOver, setClassNameForDragOver] = useState<string>('');
  const [chapterIdDragOver, setChapterIdDragOver] = useState<
    string | number | null
  >(null);

  const { isSequenceMoving } = useMoveStore.getState();

  const selectItem = (seq: { id: number }, _training?: EditorTraining) => {
    if (apiService) {
      apiService.getSequenceInfo(seq.id).then(_sequence => {
        setSequence(_sequence.sequence);

        setTranslationScope({ kind: 'sequence', sequenceId: seq.id });

        if (
          _sequence.sequenceMetadata &&
          hasFeatureFlag('course_editor_backend_undo_redo')
        ) {
          setUndoRedoState(
            _sequence.sequenceMetadata.canUndo,
            _sequence.sequenceMetadata.canRedo
          );
        }

        const trainingList = _training || training;

        if (!isMove) {
          // This runs asynchronously after getSequenceInfo resolves, so the store
          // may already have newer content-edit timestamps (e.g. set by a delete
          // that triggered this navigation). Rebuild from the stale snapshot would
          // clobber them and wrongly re-disable Publish — so keep the freshest ones.
          const latestTraining = useEditorStore.getState().training;
          const updatedTraining: EditorTraining = {
            ...trainingList!,
            contentEditedAt:
              latestTraining?.contentEditedAt ?? trainingList!.contentEditedAt,
            lastPublishedAt:
              latestTraining?.lastPublishedAt ?? trainingList!.lastPublishedAt,
            chapters: trainingList!.chapters.map((chapter: EditorChapter) => ({
              ...chapter,
              chapterSequences: chapter?.chapterSequences?.map(
                (
                  chapterSequence: EditorChapter['chapterSequences'][number]
                ) => {
                  return chapterSequence.sequence.id === seq.id
                    ? {
                        ...chapterSequence,
                        sequence: {
                          ...chapterSequence.sequence,
                          ..._sequence.sequence
                        }
                      }
                    : chapterSequence;
                }
              )
            }))
          };
          setTraining(updatedTraining);
        }
      });
    }
  };

  const moveItem = (
    targetChapterId: number | string,
    chapterSequenceId: number | string,
    hoverIndex: number
  ) => {
    setIsMove(true);
    const updatedChapters = [...training!.chapters];
    const sourceChapterIndex = updatedChapters.findIndex(chapter =>
      chapter.chapterSequences.some(
        (sequence: EditorChapter['chapterSequences'][number]) =>
          sequence.id === chapterSequenceId
      )
    );

    if (sourceChapterIndex !== -1) {
      const sourceChapter = updatedChapters[sourceChapterIndex];
      const dragIndex = sourceChapter.chapterSequences.findIndex(
        (sequence: EditorChapter['chapterSequences'][number]) =>
          sequence.id === chapterSequenceId
      );
      const [draggedItem] = sourceChapter.chapterSequences.splice(dragIndex, 1);

      const targetChapterIndex = updatedChapters.findIndex(
        chapter => chapter.id === targetChapterId
      );

      if (targetChapterIndex !== -1) {
        const targetChapter = updatedChapters[targetChapterIndex];
        targetChapter.chapterSequences.splice(hoverIndex, 0, draggedItem);

        setTraining({ ...training!, chapters: updatedChapters });
        markContentEdited();

        if (apiService) {
          apiService
            .moveSequence(
              targetChapterId as number,
              chapterSequenceId as number,
              hoverIndex + 1
            )
            .then(() => {
              setIsMove(false);
            });
        }
      }
    }
  };

  const handleShowPopupForChapter = (
    event: React.MouseEvent,
    chapterData: EditorChapter
  ) => {
    setAnchorElement(event.currentTarget as HTMLElement);
    setChapterData(chapterData);
    setIsPopupOpen(true);
    setOpenPopupId(chapterData.id as string);
  };

  const handleChapterDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    chapterId: number | string
  ) => {
    const { isSequenceMoving } = useMoveStore.getState();
    if (isSequenceMoving) {
      return;
    }
    e.dataTransfer.setData('text/plain', JSON.stringify({ chapterId }));
    useMoveStore.getState().startChapterMove(chapterId);

    if (window) {
      window.sessionStorage.setItem('dragChapterItemId', chapterId as string);
    }
  };

  const handleChapterDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetChapterId: number | string
  ) => {
    const { isSequenceMoving, chapterSequenceId } = useMoveStore.getState();

    setClassNameForDragOver('');
    setIsDragOver(false);

    if (isSequenceMoving) {
      const updatedChapters = [...training!.chapters];
      const targetChapterIndex = updatedChapters.findIndex(
        chapter => chapter.id === targetChapterId
      );

      if (
        updatedChapters[targetChapterIndex] &&
        updatedChapters[targetChapterIndex].chapterSequences.length == 0
      ) {
        moveItem(targetChapterId, chapterSequenceId!, 0);

        setTimeout(() => {
          useMoveStore.getState().stopSequenceMove();
        }, 500);
      }

      return;
    }

    const draggedData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const draggedChapterId = draggedData.chapterId;

    const updatedChapters = [...training!.chapters];

    const draggedChapterIndex = updatedChapters.findIndex(
      chapter => chapter.id === draggedChapterId
    );

    const targetChapterIndex = updatedChapters.findIndex(
      chapter => chapter.id === targetChapterId
    );

    const newTraining = {
      ...training!,
      chapters: updatedChapters
    };

    if (draggedChapterIndex !== -1 && targetChapterIndex !== -1) {
      const [draggedChapter] = updatedChapters.splice(draggedChapterIndex, 1);
      updatedChapters.splice(targetChapterIndex, 0, draggedChapter);
      setTraining(newTraining);
      markContentEdited();
    }

    if (apiService && newTraining) {
      newTraining.chapters.map((chapter: EditorChapter, index: number) => {
        if (chapter.id == draggedChapterId) {
          apiService.updateChapter(draggedChapterId as number, {
            position: index + 1
          });
        }
      });
    }

    // Clear the chapter move state after successful drop
    useMoveStore.getState().stopChapterMove();
    if (window) {
      window.sessionStorage.removeItem('dragChapterItemId');
    }
  };

  const handleChapterDragEnd = () => {
    setClassNameForDragOver('');
    setIsDragOver(false);
    setChapterIdDragOver(null);

    useMoveStore.getState().stopChapterMove();
    if (window) {
      window.sessionStorage.removeItem('dragChapterItemId');
    }
  };

  const scrollToBottom = () => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollTo({
        top: wrapperRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const onCreateNewChapter = () => {
    createNewChapter('Unlimited Chapter');
    scrollToBottom();
    setClassNameForDragOver('');
    setIsDragOver(false);
  };

  useEffect(() => {
    if (lastChapterId) {
      scrollToBottom();

      const updatedChapters = training!.chapters.map(
        (chapter: EditorChapter) => {
          if (chapter.id == lastChapterId) {
            return { ...chapter, _rename: true };
          } else {
            if (chapter._rename) {
              delete chapter._rename;
            }
            return chapter;
          }
        }
      );

      setTraining({ ...training!, chapters: updatedChapters });
    }
  }, [lastChapterId]);

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    setClassNameForDragOver('');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragOver(true);
    e.preventDefault();

    if (window) {
      const dragItemId = window.sessionStorage.getItem('dragChapterItemId');
      const currentId = e.currentTarget.dataset.id;

      if (dragItemId != currentId) {
        const dragIndex = training!.chapters.findIndex(
          (item: EditorChapter) => item.id == dragItemId
        );

        const dropIndex = training!.chapters.findIndex(
          (item: EditorChapter) => item.id == currentId
        );

        setChapterIdDragOver(Number(currentId));

        if (dragIndex === dropIndex) {
          setClassNameForDragOver('');
        } else {
          setClassNameForDragOver(
            dropIndex > dragIndex ? 'drag-over-bottom' : 'drag-over-top'
          );
        }
      } else {
        setClassNameForDragOver('');
      }
    }
  };

  return (
    <LeftPanel>
      {mode === 'translation' ? (
        <div
          style={{
            padding: '14px 16px 2px 16px',
            background: '#fff'
          }}
        >
          <ViewAllBtn
            type="button"
            className={translationScope.kind === 'all' ? 'active' : ''}
            onClick={() => setTranslationScope({ kind: 'all' })}
          >
            <StructureIconForBtn />
            View all Content
          </ViewAllBtn>
        </div>
      ) : (
        <CreateNew
          courseTitle={training?.title as string}
          onCreateNewChapter={onCreateNewChapter}
        />
      )}
      <Wrapper ref={wrapperRef}>
        <div>
          {training &&
            training.chapters &&
            training.chapters.length > 0 &&
            training.chapters.map((el: EditorChapter, i: number) => {
              const currentChapterSequencesCount = el.chapterSequences.length;
              const totalChapterSequences = training.chapters.reduce(
                (acc: number, chapter: EditorChapter) => {
                  return acc + chapter.chapterSequences.length;
                },
                0
              );

              const isDeleteDisabled =
                training.chapters.length <= 1 ||
                (currentChapterSequencesCount > 0 &&
                  currentChapterSequencesCount == totalChapterSequences);

              return (
                <div
                  key={`item_${el.id}`}
                  className={
                    isSequenceMoving
                      ? ''
                      : chapterIdDragOver == el.id
                        ? `chapterItemElement ${
                            isDragOver ? classNameForDragOver : ''
                          }`
                        : 'chapterItemElement'
                  }
                  data-id={el.id}
                  data-index={i}
                  draggable={draggable}
                  onDragStart={e => handleChapterDragStart(e, el.id!)}
                  onDragEnd={handleChapterDragEnd}
                  onDrop={e => handleChapterDrop(e, el.id!)}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <ChapterTitle>
                    <DragAndDropIconForBtn />
                    {mode === 'translation' && !el._rename ? (
                      <button
                        type="button"
                        onClick={() =>
                          setTranslationScope({
                            kind: 'chapter',
                            chapterId: el.id as number
                          })
                        }
                        className={
                          translationScope.kind === 'chapter' &&
                          translationScope.chapterId === el.id
                            ? 'chapter_title_wrapper active'
                            : 'chapter_title_wrapper'
                        }
                      >
                        <div className="chapter_number">Chapter {i + 1}</div>
                        <ChapterName el={el} setDraggable={setDraggable} />
                      </button>
                    ) : (
                      <div className="chapter_title_wrapper">
                        <div className="chapter_number">Chapter {i + 1}</div>
                        <ChapterName el={el} setDraggable={setDraggable} />
                      </div>
                    )}
                    {!el._rename && (
                      <DotsIconForBtn
                        className="chapter_dots"
                        onClick={event => handleShowPopupForChapter(event, el)}
                      />
                    )}
                    <PopupMenuForChapter
                      key={`chapter_popup_${el.id}`}
                      isOpen={isPopupOpen && openPopupId === el.id}
                      anchorElement={anchorElement}
                      onClose={() => setIsPopupOpen(false)}
                      data={chapterData!}
                      apiService={apiService}
                      training={training!}
                      setTraining={setTraining}
                      id={el.id as string}
                      isDeleteDisabled={isDeleteDisabled}
                    />
                  </ChapterTitle>
                  {el?.chapterSequences?.map(
                    (
                      seq: EditorChapter['chapterSequences'][number],
                      i: number
                    ) => {
                      return (
                        <SequenceItem
                          key={`item_${i}`}
                          selectItem={selectItem}
                          data={seq}
                          index={i}
                          moveItem={moveItem}
                          chapterId={el.id!}
                          chapterSequenceId={seq.id!}
                          setDraggable={setDraggable}
                          draggable={draggable}
                          mode={mode}
                        />
                      );
                    }
                  )}
                </div>
              );
            })}
        </div>
      </Wrapper>
    </LeftPanel>
  );
};
